"use client";

import React, { useEffect, useMemo, useRef } from "react";
import {
  Button,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  useOverlayState,
  TextArea,
  Link,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownPopover,
  DropdownSection,
  DropdownTrigger,
  cn,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth, useUser } from "@webuild-ai/react-auth";
import { useBrand } from "@webuild-ai/react-ui/hooks";
import { BrandLogo } from "@webuild-ai/react-ui/components";

import { ModalOverlay, ModalHeader, ModalBody } from "@/components/UI/ModalOverlay";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { availableLanguages } from "@/i18n";
import { useTheme } from "@/hooks/use-theme";
import { useGlobalStore } from "@/stores/GlobalStore";
import { useChatStore } from "@/stores/ChatStore";
import { useChatHistoryStore } from "@/stores/ChatHistoryStore";
import { useChatURLState } from "@/hooks/useChatURLState";
import { useAppLogout } from "@/hooks/useAppLogout";
import { logger } from "@/telemetry";

import { ModelInfoIcon } from "../icons";
import { ChatHistorySearchModal } from "../Chat/ChatHistorySearchModal";
import { SearchChatButton } from "../Chat/SearchChatButton";
import { ChatHistoryComponent } from "../Chat/ChatHistory";
import { I18nIcon, LanguageSwitch } from "../language-switch";

import { useSidebar } from "./SidebarContext";
import { useSidebarMenuItems } from "./SidebarMenuItems";
import Sidebar from "./Sidebar";

export function SidebarLayout({ children }: { children: React.ReactNode }) {
  // Auth and user hooks
  const { userProfile } = useUser();
  const { user, isAuthenticated } = useAuth();
  const logout = useAppLogout();

  const { adminClient } = useWebuildAPIClient();

  // Store hooks
  const { newChat, load, triggerResetPrompt } = useChatStore();
  const {
    fetchChatHistory,
    displayedChats,
    chatHistory,
    selectChat,
    selectAndEnsureVisible,
    clearSelection,
  } = useChatHistoryStore();
  const { sidebar } = useGlobalStore();
  const { isSidebarCompact, toggleSidebarCompact } = sidebar;

  // Navigation hooks
  const location = useLocation();
  const navigate = useNavigate();
  const { workflowId: urlWorkflowId } = useParams<{ workflowId?: string }>();
  const { navigateToChat, clearChatId, chatId: chatIdFromURL } = useChatURLState();

  // UI hooks
  const { t } = useTranslation();
  const { showChatHistory, setShowChatHistory } = useSidebar();

  // Derived values
  const pathname = location.pathname;

  // Determine the current workflow ID from URL or default to chat_agent
  const currentWorkflowId = urlWorkflowId || "chat_agent";

  useEffect(() => {
    const shouldShowHistory = location.pathname.startsWith("/chat");

    if (shouldShowHistory !== showChatHistory) {
      setShowChatHistory(shouldShowHistory);
    }
  }, [location.pathname, showChatHistory, setShowChatHistory]);

  // Track the last chat ID we handled from URL to avoid re-processing on displayedChats changes
  const lastHandledUrlChatIdRef = useRef<string | null>(null);

  // Select chat from URL when chat history is loaded (initial page load only)
  useEffect(() => {
    // Skip if no chat ID in URL or already handled this chat ID
    if (!chatIdFromURL || !adminClient || displayedChats.length === 0) {
      return;
    }

    // Skip if we already handled this chat ID (prevents re-running on displayedChats changes)
    if (lastHandledUrlChatIdRef.current === chatIdFromURL) {
      return;
    }

    logger.info("🔗 SidebarLayout: URL has chat ID, selecting and loading", { chatIdFromURL });
    lastHandledUrlChatIdRef.current = chatIdFromURL;

    // Use selectAndEnsureVisible to fetch the chat if it's not in the displayed list
    selectAndEnsureVisible(chatIdFromURL, adminClient);

    // Load the chat data if it's not already loaded
    load(adminClient, chatIdFromURL);
  }, [chatIdFromURL, displayedChats.length, selectAndEnsureVisible, adminClient, load]);

  const handleBackToMenu = () => {
    logger.info("🔙 Back button clicked - navigating back in history");

    // Clear chat selection
    clearSelection();

    // Use browser's back navigation to go to previous page
    navigate(-1);
  };

  const {
    isOpen: isSearchModalOpen,
    open: onSearchModalOpen,
    setOpen: onSearchModalSetOpen,
  } = useOverlayState();

  // Keyboard shortcut: Cmd+K (Mac) / Ctrl+K (Windows/Linux) to open search
  useEffect(() => {
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      // Check both lowercase and uppercase 'k'/'K'
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        // Only trigger if on chat page with history visible
        if (showChatHistory && displayedChats.length > 0) {
          e.preventDefault(); // Prevent browser default (Chrome address bar focus)
          e.stopPropagation();
          onSearchModalOpen();
        }
      }
    };

    // Use capture phase to intercept events before they reach child elements
    document.addEventListener("keydown", handleKeyDown, true);

    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [showChatHistory, displayedChats.length, onSearchModalOpen]);

  const handleSelectChat = async (chatHistoryId: string) => {
    if (!adminClient) return;

    // Mark this chat ID as handled BEFORE navigation to prevent URL effect from re-processing
    lastHandledUrlChatIdRef.current = chatHistoryId;

    try {
      // Validate chat exists in history
      const isValid = selectChat(chatHistoryId);

      if (!isValid) {
        logger.error("Chat not found in history");

        return;
      }

      // Navigate to chat URL (IMMEDIATE - user requirement)
      navigateToChat(chatHistoryId, currentWorkflowId);

      // Load chat data from backend
      await load(adminClient, chatHistoryId);
      triggerResetPrompt();
    } catch (error: any) {
      logger.error("Failed to load chat", error);

      // Handle specific HTTP error codes
      if (error?.status === 403) {
        logger.error("You don't have access to this chat");
      } else if (error?.status === 404) {
        logger.error("Chat not found or has been deleted");
      } else {
        logger.error("Failed to load chat. Please try again.");
      }

      // Navigate back to base route on error
      const basePath =
        currentWorkflowId === "chat_agent" ? "/chat" : `/workflows/${currentWorkflowId}`;

      navigate(basePath, { replace: true });
    }
  };

  /**
   * Handler for search modal selections.
   * Unlike handleSelectChat, this uses selectAndEnsureVisible to handle
   * chats that may not be in the local cache (search results from API).
   */
  const handleSearchSelectChat = async (chatHistoryId: string) => {
    if (!adminClient) return;

    // Mark this chat ID as handled BEFORE navigation to prevent URL effect from re-processing
    lastHandledUrlChatIdRef.current = chatHistoryId;

    try {
      // Use selectAndEnsureVisible to fetch the chat if it's not in the displayed list
      // This is necessary for search results which may include chats not in local cache
      await selectAndEnsureVisible(chatHistoryId, adminClient);

      // Navigate to chat URL (IMMEDIATE - user requirement)
      navigateToChat(chatHistoryId, currentWorkflowId);

      // Load chat data from backend
      await load(adminClient, chatHistoryId);
      triggerResetPrompt();
    } catch (error: any) {
      logger.error("Failed to load chat from search", error);

      // Handle specific HTTP error codes
      if (error?.status === 403) {
        logger.error("You don't have access to this chat");
      } else if (error?.status === 404) {
        logger.error("Chat not found or has been deleted");
      } else {
        logger.error("Failed to load chat. Please try again.");
      }

      // Navigate back to base route on error
      const basePath =
        currentWorkflowId === "chat_agent" ? "/chat" : `/workflows/${currentWorkflowId}`;

      navigate(basePath, { replace: true });
    }
  };

  const handleNewChat = () => {
    logger.info("🆕 handleNewChat: Starting new chat");

    // Clear the chat ID from URL first
    clearChatId();

    // Create a new chat session
    newChat();

    // Clear the chat history sidebar selection
    clearSelection();

    triggerResetPrompt();

    // Focus the chat input field after a short delay
    setTimeout(() => {
      const textarea = document.querySelector(
        'textarea[placeholder*="Start new chat"]',
      ) as HTMLTextAreaElement;

      if (textarea) {
        textarea.focus();
      }
    }, 150);

    // Fetch chat history again to refresh the list (after clearing selection)
    // Use setTimeout to ensure clearSelection() completes first
    if (adminClient && user?.id) {
      setTimeout(() => {
        fetchChatHistory(user.id, adminClient);
      }, 10);
    }
  };

  // Use secure sidebar menu items that validate permissions server-side
  const { menuItems: sidebarMenuItems } = useSidebarMenuItems();

  // Match the active sidebar item by longest `href` prefix against the current
  // pathname. This avoids relying on path-segment ↔ item-key coincidence
  // (e.g. `/datahub` → item key `data`, `/metrics` → item key `monitoring`,
  // NGV's `/ncrm` → item key `dashboard`).
  const currentKey = useMemo<string | undefined>(() => {
    const flat: { key: string; href: string }[] = [];
    const walk = (items: typeof sidebarMenuItems) => {
      items.forEach((item) => {
        if (item.items && item.items.length > 0) walk(item.items);
        if (item.href) flat.push({ key: item.key, href: item.href });
      });
    };

    walk(sidebarMenuItems);

    const match = flat
      .filter(({ href }) => pathname === href || pathname.startsWith(`${href}/`))
      .sort((a, b) => b.href.length - a.href.length)[0];

    return match?.key;
  }, [sidebarMenuItems, pathname]);

  const {
    isOpen: isModalOpen,
    open: onModalOpen,
    close: onModalClose,
    setOpen: onModalSetOpen,
  } = useOverlayState();
  const {
    isOpen: isHelpModalOpen,
    open: onHelpModalOpen,
    setOpen: onHelpModalSetOpen,
  } = useOverlayState();

  const { brand } = useBrand();
  const { isDark } = useTheme();

  const handleFeedbackSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const feedback = formData.get("feedback") as string;

    try {
      if (!adminClient) {
        throw new Error("Admin client not found");
      }
      await adminClient.feedback.createFeedback({
        feedback,
      });
      onModalClose();
    } catch (error) {
      logger.error("Failed to submit feedback", error);
    }
  };

  return (
    <div className="flex h-dvh w-full">
      {isAuthenticated && brand.name && userProfile?.name && (
        <div
          className={cn(
            "relative flex h-full flex-col border-r border-default-200 transition-[width] duration-150 ease-out motion-reduce:transition-none",
            // The expanded width scales with the viewport. The floor is the previous
            // w-56, so the sidebar never becomes narrower than it used to be, and the
            // cap stops it sprawling on ultrawide displays. Tailwind v4 arbitrary
            // values must carry no spaces inside the brackets or nothing is generated.
            isSidebarCompact ? "w-16 items-center px-2" : "w-[clamp(14rem,16%,20rem)]",
          )}
        >
          {/* Sidebar header: logo and toggle button with smooth transition */}
          {isSidebarCompact ? (
            // Collapsed: logo on top, toggle button below
            <div className="flex flex-col items-center pt-4 gap-2">
              {/* Logo */}
              <Link
                aria-label="Home"
                className="flex items-center justify-center"
                color="foreground"
                href="/"
              >
                {/* variant="primary" (not "small") so a brand with only a dark
                    primary logo (webuild) resolves to logoUrls.primaryDark in
                    dark mode. react-ui's "small" variant has no dark fallback and
                    would render the light logo on the dark sidebar; no brand ships
                    a distinct small logo, so light mode is unchanged. */}
                <BrandLogo style={{ height: 32 }} variant="primary" />
              </Link>
              {/* Toggle Button */}
              <Button
                isIconOnly
                aria-expanded={false}
                aria-label={t("Expand sidebar")}
                className="m-0! w-11! h-11! min-w-0!"
                data-testid="sidebar-toggle"
                size="sm"
                variant="ghost"
                onPress={toggleSidebarCompact}
              >
                <Icon
                  color={isDark ? brand.colours.dark.primary : brand.colours.light.primary}
                  height={32}
                  icon="solar:sidebar-minimalistic-linear"
                  style={{ width: 32, height: 32 }}
                  width={32}
                />
              </Button>
            </div>
          ) : (
            // Expanded: logo left, toggle right
            <div className="flex items-center justify-between p-4 w-full">
              <Link aria-label="Home" className="flex items-center p-1" color="foreground" href="/">
                {/* variant="primary" (not "small") so a brand with only a dark
                    primary logo (webuild) resolves to logoUrls.primaryDark in
                    dark mode; see the collapsed logo above for the rationale. */}
                <BrandLogo style={{ height: 40 }} variant="primary" />
              </Link>
              <Button
                isIconOnly
                aria-expanded={true}
                aria-label={t("Collapse sidebar")}
                className="w-11! h-11! min-w-0!"
                data-testid="sidebar-toggle"
                size="sm"
                variant="ghost"
                onPress={toggleSidebarCompact}
              >
                <Icon
                  color={isDark ? brand.colours.dark.primary : brand.colours.light.primary}
                  height={36}
                  icon="solar:sidebar-minimalistic-linear"
                  style={{ width: 36, height: 36 }}
                  width={36}
                />
              </Button>
            </div>
          )}
          {/* End sidebar header */}
          {showChatHistory && !isSidebarCompact && (
            <div className="flex flex-col">
              <button
                aria-label="Back to menu"
                className="group mx-4 mt-2 mb-3 flex w-fit items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-brand-primary transition-[background-color,box-shadow,color] duration-200 hover:bg-default-100 hover:shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary dark:hover:bg-default-200/20 cursor-pointer"
                type="button"
                onClick={handleBackToMenu}
              >
                <Icon
                  className="transition-transform group-hover:-translate-x-0.5"
                  icon="solar:alt-arrow-left-linear"
                  width={18}
                />
                <span className="underline-offset-2 group-hover:underline">{t("Back")}</span>
              </button>
              <div className="w-full px-4 pb-2 flex flex-col gap-2">
                <Button
                  className="btn-brand-primary h-[36px] w-full text-sm"
                  data-id="new-chat-button"
                  size="sm"
                  onPress={handleNewChat}
                >
                  <Icon
                    className="text-default-50"
                    icon="solar:chat-round-dots-linear"
                    width={20}
                  />{" "}
                  {t("New Chat")}
                </Button>
              </div>
            </div>
          )}
          <div className="flex-1 flex flex-col min-h-0">
            <Sidebar
              className="px-2 py-1"
              defaultSelectedKey="home"
              isCompact={isSidebarCompact}
              items={sidebarMenuItems}
              selectedKeys={currentKey ? [currentKey] : []}
              showMenuTitle={false}
            />
            {isSidebarCompact && showChatHistory && displayedChats.length > 0 && (
              <div className="flex justify-center">
                <Tooltip>
                  <TooltipTrigger>
                    <Button
                      isIconOnly
                      aria-label={t("Search chat history")}
                      className="btn-brand-primary !w-10 !h-10 !min-w-0 rounded-xl"
                      data-id="compact-search-button"
                      size="sm"
                      variant="primary"
                      onPress={onSearchModalOpen}
                    >
                      <Icon className="text-white" icon="solar:magnifer-linear" width={20} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent placement="right">{t("Search chat history")}</TooltipContent>
                </Tooltip>
              </div>
            )}
            {!isSidebarCompact && showChatHistory && (
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 flex-shrink-0">
                  <span className="font-bold text-brand-primary text-sm dark:text-foreground!">
                    {t("Chat History")}
                  </span>
                  <SearchChatButton
                    isCompact={true}
                    isDisabled={displayedChats.length === 0}
                    onOpenSearch={onSearchModalOpen}
                  />
                </div>
                <div className="flex-1 min-h-0">
                  <ChatHistoryComponent
                    isShowHistory={true}
                    sectionClasses={{
                      base: "px-1",
                    }}
                    showScroll={true}
                    onNewChat={handleNewChat}
                    onSelectChat={handleSelectChat}
                  />
                </div>
              </div>
            )}
          </div>
          <div
            className={cn("mt-auto flex flex-col flex-shrink-0 pb-6", {
              "items-center": isSidebarCompact,
            })}
            data-id="sidebar-footer-container"
          >
            {userProfile?.name && (
              <div className="flex flex-col gap-4 mb-4 px-4" data-id="user-profile-container">
                <Dropdown data-id="user-dropdown" placement="top-end">
                  <DropdownTrigger>
                    {/* Tooltip is enabled only when the sidebar is collapsed: the name is
                        hidden in compact mode, so hovering the avatar surfaces who is signed
                        in. Wrapping the div (not the trigger Button) keeps the dropdown's
                        press behaviour intact. */}
                    <Tooltip isDisabled={!isSidebarCompact}>
                      <TooltipTrigger>
                        <div
                          className={`flex min-h-[44px] h-auto justify-start gap-2 bg-transparent px-0 py-2 whitespace-normal cursor-pointer items-center ${isSidebarCompact ? "justify-center border-0" : ""}`}
                          data-id="user-dropdown-button"
                          role="button"
                          tabIndex={0}
                        >
                          <div
                            className={`flex w-full items-center gap-2 ${isSidebarCompact && "justify-center"}`}
                            data-id="user-info"
                          >
                            <div
                              className={cn(
                                "flex-shrink-0 rounded-full bg-default-800 flex items-center justify-center",
                                isSidebarCompact ? "w-9 h-9" : "w-7 h-7",
                              )}
                              data-id="user-avatar"
                            >
                              <Icon
                                className="text-default-200"
                                icon="solar:user-bold"
                                width={isSidebarCompact ? 20 : 14}
                              />
                            </div>
                            {!isSidebarCompact && (
                              <p
                                className="text-xs text-foreground break-words"
                                data-id="user-name"
                              >
                                {userProfile?.name}
                              </p>
                            )}
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent placement="right">
                        <div className="flex flex-col" data-id="user-info-tooltip">
                          <span className="text-xs font-medium">{userProfile?.name}</span>
                          {userProfile?.email && (
                            <span className="text-tiny opacity-70">{userProfile.email}</span>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </DropdownTrigger>
                  <DropdownPopover placement="top end">
                    <DropdownMenu
                      aria-label="Profile Actions"
                      className="w-[210px] bg-content1 px-[8px] py-[8px]"
                      data-id="profile-dropdown-menu"
                      variant="secondary"
                    >
                      <DropdownSection
                        aria-label="profile-section-3"
                        className="mb-0"
                        data-id="dropdown-section-3"
                      >
                        <DropdownItem
                          key="model-info"
                          className="py-[4px] text-default-500"
                          data-id="dropdown-item-model-info"
                          textValue="Models Overview"
                          onPress={() => navigate("/models")}
                        >
                          <div className="flex items-center gap-2">
                            <ModelInfoIcon size={20} />
                            <span className="text-small font-medium text-default-500">
                              {t("Models Overview")}
                            </span>
                          </div>
                        </DropdownItem>

                        <DropdownItem
                          key="settings"
                          className="py-[4px] text-default-500"
                          data-id="dropdown-item-settings"
                          textValue="Settings"
                          onPress={() => navigate("/settings")}
                        >
                          <div className="flex items-center gap-2">
                            <Icon icon="solar:settings-outline" width={20} />
                            <span className="text-small font-medium text-default-500">
                              {t("Settings")}
                            </span>
                          </div>
                        </DropdownItem>
                        <DropdownItem
                          key="logout"
                          className="pt-[4px] text-default-500"
                          data-id="dropdown-item-logout"
                          textValue="Log Out"
                          onPress={() => logout("user_requested")}
                        >
                          <div className="flex items-center gap-2">
                            <Icon icon="solar:logout-3-outline" width={20} />
                            <span className="text-small font-medium text-default-500">
                              {t("Log Out")}
                            </span>
                          </div>
                        </DropdownItem>
                      </DropdownSection>
                    </DropdownMenu>
                  </DropdownPopover>
                </Dropdown>
              </div>
            )}
            <div className="px-4">
              <LanguageSwitch
                availableLanguages={availableLanguages}
                data-id="language-switch"
                icon={I18nIcon}
                iconSize={24}
                isSidebarCompact={isSidebarCompact}
              />
            </div>
            {brand.features.needHelpLink && (
              <Tooltip isDisabled={!isSidebarCompact}>
                <TooltipTrigger>
                  <Button
                    fullWidth
                    aria-label={t("Provide feedback")}
                    className={cn(
                      "justify-start truncate text-default-500 data-[hovered]:text-foreground",
                      {
                        "justify-center": isSidebarCompact,
                      },
                    )}
                    isIconOnly={isSidebarCompact}
                    variant="ghost"
                    onPress={onHelpModalOpen}
                  >
                    <Icon
                      className="flex-none text-default-500"
                      icon="solar:call-chat-rounded-linear"
                      width={28}
                    />
                    {!isSidebarCompact && t("Need Help")}
                  </Button>
                </TooltipTrigger>
                <TooltipContent placement="right">{t("Need Help")}</TooltipContent>
              </Tooltip>
            )}
            <Tooltip isDisabled={!isSidebarCompact}>
              <TooltipTrigger>
                <Button
                  aria-label={t("Provide feedback")}
                  className={cn(
                    "justify-center gap-2 truncate text-foreground data-hovered:text-foreground",
                    {
                      "w-14! h-14! min-w-0! rounded-lg!": isSidebarCompact,
                    },
                  )}
                  fullWidth={!isSidebarCompact}
                  isIconOnly={isSidebarCompact}
                  variant="ghost"
                  onPress={onModalOpen}
                >
                  <Icon
                    className="flex-none text-default-600"
                    icon="solar:question-circle-bold-duotone"
                    style={{
                      width: isSidebarCompact ? 48 : 28,
                      height: isSidebarCompact ? 48 : 28,
                    }}
                    width={isSidebarCompact ? 48 : 28}
                  />
                  {!isSidebarCompact && t("Feedback")}
                </Button>
              </TooltipTrigger>
              <TooltipContent placement="right">{t("Feedback")}</TooltipContent>
            </Tooltip>

            <div className="pb-2" />

            {isModalOpen && (
              <ModalOverlay isOpen={isModalOpen} size="2xl" onOpenChange={onModalSetOpen}>
                <ModalHeader className="flex flex-col gap-1">{t("Feedback")}</ModalHeader>
                <ModalBody>
                  <form onSubmit={handleFeedbackSubmit}>
                    <label className="text-sm font-medium">{t("Your Feedback")}</label>
                    <TextArea
                      aria-label="Feedback input"
                      className="w-full"
                      minRows={3}
                      name="feedback"
                      placeholder={t("Enter your feedback here...")}
                    />
                    <div className="flex justify-end gap-2 mt-4">
                      <Button
                        aria-label="Close feedback modal"
                        className="text-white"
                        style={{
                          backgroundColor: isDark
                            ? brand.colours.dark.accent
                            : brand.colours.light.accent,
                        }}
                        variant="primary"
                        onPress={() => onModalSetOpen(false)}
                      >
                        {t("Close")}
                      </Button>
                      <Button
                        aria-label="Submit feedback"
                        className="btn-brand-primary"
                        type="submit"
                      >
                        {t("Submit")}
                      </Button>
                    </div>
                  </form>
                </ModalBody>
              </ModalOverlay>
            )}

            {isHelpModalOpen && (
              <ModalOverlay isOpen={isHelpModalOpen} size="lg" onOpenChange={onHelpModalSetOpen}>
                <ModalHeader className="flex flex-col gap-1 text-lg font-bold text-primary">
                  {t("Need Help?")}
                </ModalHeader>
                <ModalBody>
                  <div className="flex flex-col gap-4 text-base text-default-700">
                    <div className="text-default-700">
                      {t(
                        "If you need assistance, our support team is here to help you. You can reach us through the following channel:",
                      )}
                    </div>
                    <div className="flex flex-col gap-2 items-center">
                      <Button
                        as="a"
                        className="w-full max-w-xs font-semibold text-base btn-brand-primary"
                        href={brand.features.needHelpLink ?? undefined}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        <Icon height={22} icon="solar:call-chat-rounded-linear" width={22} />{" "}
                        {t("Live Chat with Support")}
                      </Button>
                      <div className="text-xs text-default-500 text-center mt-2">
                        {t("We are available and happy to assist you!")}
                      </div>
                    </div>
                  </div>
                </ModalBody>
              </ModalOverlay>
            )}
          </div>
        </div>
      )}

      {/* Chat History Search Modal */}
      <ChatHistorySearchModal
        chatHistory={chatHistory}
        isOpen={isSearchModalOpen}
        onOpenChange={onSearchModalSetOpen}
        onSelectChat={handleSearchSelectChat}
      />

      <div className="w-full flex-1 flex-col overflow-hidden">
        <main className="h-full w-full overflow-auto">{children}</main>
      </div>
    </div>
  );
}
